import {toJS} from 'mobx';
import {Parameter, ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {QuoteHistory, QuoteHistoryData} from '../../../Core/mobx/ParameterStore/types';
import RootStore from '../../../Core/mobx/RootStore';

interface Params {
    carrierParam?: Parameter;
    firstProductTypeParam?: Parameter;
    firstCoverageAmountParam?: Parameter;
    firstMonthlyPremiumParam?: Parameter;
}

const productTypeMapping: {[key: string]: string} = {
    '10': '_10yearTerm',
    '15': '_15yearTerm',
    '20': '_20yearTerm',
    '25': '_25yearTerm',
    '30': '_30yearTerm',
    '35': '_35yearTerm',
    '40': '_40yearTerm',
    gul: 'GUL',
    guaranteed: 'GuaranteedIssue',
    accidental: 'Accidental',
    vul: 'VUL',
    iul: 'IUL',
    lswiul: 'IUL',
};
const carrierNameMapping: {[key: string]: string} = {
    nationallife: 'NationalLifeGroup',
    ameritas: 'Ameritas',
    equitable: 'Equitable',
    northamerican: 'NorthAmerican',
    pacificlife: 'PacificLife',
    gerber: 'Gerber',
    vivalife: 'VivaLife',
    greatwestern: 'GreatWestern',
    mutualofomaha: 'MutualofOmaha',
    allianz: 'Allianz',
    assurity: 'Assurity',
    principal: 'Principal',
    protective: 'Protective',
    prudential: 'Prudential',
    unitedhomelife: 'UnitedHomeLife',
};

// Function to map product types and carrier names based on input values
export const mapValue = (value: string, mapping: {[key: string]: string}): string => {
    const cleanedValue = value.toLowerCase().replace(/\s/g, '');

    let result = 'unknown';
    Object.keys(mapping).forEach((key) => {
        if (cleanedValue.includes(key)) {
            result = mapping[key];
        }
    });

    return result;
};

export function getParams(isCombo: boolean, parameterStore: ParameterStore): Params {
    const params: Params = {};

    params.carrierParam = isCombo
        ? parameterStore.approval.secondCarrierQuoted
        : (parameterStore.approval.firstCarrierQuoted as Parameter | undefined);

    params.firstProductTypeParam = isCombo
        ? parameterStore.approval.secondProductType
        : (parameterStore.approval.firstProductType as Parameter | undefined);

    params.firstCoverageAmountParam = isCombo
        ? parameterStore.approval.secondCoverageAmount
        : (parameterStore.approval.firstCoverageAmount as Parameter | undefined);

    params.firstMonthlyPremiumParam = isCombo
        ? parameterStore.approval.secondMonthlyPremium
        : (parameterStore.approval.firstMonthlyPremium as Parameter | undefined);

    return params;
}

// Function to extract a value from the 'out' property of a quote
export const getValueFromOut = (fieldName: string, quote: QuoteHistoryData): string | number | null => {
    const {value} = toJS(quote).out.find((o: {name: string}) => o.name === fieldName) || {value: null};
    return value;
};

// Function to process a quote and update UI parameters
export const processQuote = (
    quote: QuoteHistoryData | null,
    isCombo: boolean,
    parameterStore: ParameterStore,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    changeValue: (p?: Parameter | undefined, value?: any) => void,
) => {
    // Find relevant UI parameters based on whether it's a combo quote
    const {carrierParam, firstProductTypeParam, firstCoverageAmountParam, firstMonthlyPremiumParam} = getParams(
        isCombo,
        parameterStore,
    );
    let productType;
    let faceAmount;
    let carrierName;
    let premium;
    if (quote) {
        switch (quote.productType) {
            case 'Term':
                productType = mapValue(quote.productName, productTypeMapping);
                faceAmount = getValueFromOut('Face amount', quote);
                carrierName = mapValue(getValueFromOut('Carrier', quote) as string, carrierNameMapping);
                premium = getValueFromOut('Monthly premium', quote);
                break;
            case 'IUL':
            case 'LSWIUL':
                productType = mapValue(quote.productType, productTypeMapping);
                faceAmount = getValueFromOut('Initial death benefit', quote);
                carrierName = mapValue(getValueFromOut('Carrier', quote) as string, carrierNameMapping);
                premium = getValueFromOut('Initial monthly premium', quote);
                break;
            case 'Guaranteed':
                productType = mapValue(quote.productType, productTypeMapping);
                faceAmount = getValueFromOut('Face amount', quote);
                carrierName = mapValue(getValueFromOut('Carrier Product', quote) as string, carrierNameMapping);
                premium = getValueFromOut('Monthly premium', quote);

                break;
            default:
                // Default values for unknown product types
                faceAmount = 0;
                carrierName = 'unknown';
                premium = 0;
        }
        // Update UI parameters based on the processed quote data
        if (carrierParam) {
            // Don't overwrite a valid firstCarrierQuoted value with "unknown"
            if (!isCombo && carrierName === 'unknown' && carrierParam.value && carrierParam.value !== 'unknown') {
                console.log('copyToPreapproval: Not overwriting valid firstCarrierQuoted with "unknown"', {
                    currentValue: carrierParam.value,
                    skippedValue: carrierName,
                });
            } else {
                changeValue(carrierParam, carrierName || '');
            }
        }
        if (firstProductTypeParam) changeValue(firstProductTypeParam, productType || '');
        if (firstCoverageAmountParam) changeValue(firstCoverageAmountParam, faceAmount || 0);
        if (firstMonthlyPremiumParam) changeValue(firstMonthlyPremiumParam, premium || 0);
    }
};

export const copyToPreapproval = (parameterStore: ParameterStore, data: QuoteHistory | null): void => {
    // Function to trigger changes in the UI based on pre-approval rules
    const changeValue = RootStore.agentPortalStore.parameterStore.onValueChangedPreApproval;
    const isCombination = !!data?.combinedQuote;

    // Check if there is data available
    if (data) {
        const {singleQuote, combinedQuote} = data;

        // get secondary params to clear them
        const {carrierParam, firstProductTypeParam, firstCoverageAmountParam, firstMonthlyPremiumParam} = getParams(
            true,
            parameterStore,
        );

        if (carrierParam) changeValue(carrierParam, '');
        if (firstProductTypeParam) changeValue(firstProductTypeParam, '');
        if (firstCoverageAmountParam) changeValue(firstCoverageAmountParam, '');
        if (firstMonthlyPremiumParam) changeValue(firstMonthlyPremiumParam, '');

        // Process single and/or combined quotes
        if (isCombination) {
            processQuote(combinedQuote, true, parameterStore, changeValue);
        }

        // clear secondary values
        processQuote(singleQuote, false, parameterStore, changeValue);
    }
};
